import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportFatal, flushSentry } from '../lib/sentry'

/**
 * Last-line-of-defense error boundary for the TV display.
 *
 * Why this exists:
 *   The TV runs unattended for 12+ hours a day in a gym. Without a
 *   boundary, any uncaught render error unmounts the entire React tree
 *   and leaves a permanently black screen — indistinguishable from a
 *   powered-off TV, and nobody notices until a coach wonders where the
 *   leaderboard went. (Observed exactly this during dev: one crash,
 *   black screen, no recovery until manual reload.)
 *
 * Recovery strategy — reload, don't retry render:
 *   A React "retry" (clearing the error state) usually re-crashes
 *   immediately because the bad state that caused the throw is still in
 *   memory. A full location.reload() restarts from persisted state —
 *   the pairing token lives in localStorage, so the TV re-pairs itself,
 *   re-joins its room, and re-fetches the active session snapshot
 *   without human intervention. Total outage ≈ RELOAD_AFTER_S + boot
 *   time (~12s), acceptable for a display surface.
 *
 * Crash-loop guard:
 *   If the error is deterministic on boot (bad deploy), naive reload
 *   spins forever and looks like flickering. We count recent reloads in
 *   sessionStorage (cleared when the tab is closed / TV power-cycled):
 *   after MAX_RELOADS within the window we stop auto-reloading and park
 *   on the error screen with the pairing-era instruction to power-cycle,
 *   so a human can notice and report instead of the TV strobing.
 */

const RELOAD_AFTER_S = 10
const MAX_RELOADS = 3
const RELOAD_WINDOW_MS = 5 * 60_000
const STORAGE_KEY = 'agaru.tv.crash-reloads.v1'

function recentReloads(): number[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    const arr: number[] = raw ? JSON.parse(raw) : []
    const cutoff = Date.now() - RELOAD_WINDOW_MS
    return arr.filter((t) => t >= cutoff)
  } catch {
    return []
  }
}

interface State {
  hasError: boolean
  /** Seconds until auto-reload; null = crash-looped, no auto-reload. */
  countdown: number | null
}

export default class TvErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, countdown: RELOAD_AFTER_S }
  private timer: ReturnType<typeof setInterval> | null = null

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[TV] fatal render error', error, info.componentStack)
    // Report BEFORE the reload countdown starts — the reload destroys the
    // document and any unflushed event with it. flushSentry is capped at
    // 2s so a dead network can't stall recovery; fire-and-forget is fine
    // because the countdown gives it 10s of runway anyway.
    reportFatal(error, info.componentStack)
    void flushSentry()

    const reloads = recentReloads()
    if (reloads.length >= MAX_RELOADS) {
      // Crash loop — park.
      this.setState({ countdown: null })
      return
    }

    this.setState({ countdown: RELOAD_AFTER_S })
    this.timer = setInterval(() => {
      this.setState((prev) => {
        const next = (prev.countdown ?? 1) - 1
        if (next <= 0) {
          try {
            sessionStorage.setItem(
              STORAGE_KEY,
              JSON.stringify([...recentReloads(), Date.now()]),
            )
          } catch {
            /* storage unavailable — reload anyway */
          }
          window.location.reload()
        }
        return { ...prev, countdown: next }
      })
    }, 1000)
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const looped = this.state.countdown === null
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          background: '#0E1022',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <div style={{ fontSize: 56, fontWeight: 900, color: '#FF7A6E', letterSpacing: -1 }}>
          Agaru
        </div>
        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 20, marginTop: 24, fontWeight: 600 }}>
          {looped ? 'Display needs attention' : 'Something went wrong'}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 15, marginTop: 8, maxWidth: 480, textAlign: 'center', lineHeight: 1.5 }}>
          {looped
            ? 'The display restarted several times without recovering. Please power-cycle the TV or contact support.'
            : `Restarting automatically in ${this.state.countdown}s — no action needed.`}
        </p>
      </div>
    )
  }
}
